import { useReducer } from 'react';
import moment from 'moment';

const SEARCH = 'SEARCH';
const DATA_SET = 'DATA_SET';
const ITEM_SELECT = 'ITEM_SELECT';
const CLEAR_SELECTED = 'CLEAR_SELECTED';
const SELECTED_SET = 'SELECTED_SET';
const SET_PAGE = 'SET_PAGE';
const SET_LIMIT = 'SET_LIMIT';
const REPLACE_ITEM = 'REPLACE_ITEM';
const SORT = 'SORT';

// let initialData = [];

const initialState = {
	initialized: false,
	data: [],
	filtered: [],
	selected: [],
	searched: [],
	key: null,
	searchTerm: '',
	additionalFiltering: [],
	searchColumns: [],
	sortBy: null,
	sortDirection: 'desc',
	filters: [],
	// <-- pagination --> //
	limit: null,
	currentPage: 0,
	pageCount: 0
};

function reducer(state = initialState, action) {
	switch (action.type) {
		case DATA_SET: {
			const initialData = applyFilters(action.data, state);
			const searched = state.searchTerm.length > 0 ? doSearch(state.searchTerm, state.searchColumns, initialData) : initialData;
			const filtered = state.additionalFiltering.length > 0 ? doFiltering(state.additionalFiltering, state.searchColumns, searched) : searched;
			const { paginatedData, pageCount } = applyPagination(filtered, { ...state, currentPage: action.page !== undefined ? action.page : state.currentPage });
			return {
				...state,
				initialized: true,
				data: initialData,
				filtered: paginatedData,
				pageCount,
				currentPage: action.page !== undefined ? action.page : state.currentPage
			};
		}
		case SEARCH: {
			const searched = doSearch(action.searchTerm, state.searchColumns, state.data);
			const dataToFilter = action.searchTerm.length > 0 ? searched : state.data;
			const filtered = doFiltering(action.additionalFiltering, state.searchColumns, dataToFilter);
			const dataToPaginate = action.additionalFiltering && action.additionalFiltering.length > 0 ? filtered : dataToFilter;
			const { paginatedData, pageCount } = applyPagination(dataToPaginate, { ...state, currentPage: 0 });

			return {
				...state,
				filtered: paginatedData,
				searched,
				searchTerm: action.searchTerm,
				additionalFiltering: action.additionalFiltering ? action.additionalFiltering : [],
				pageCount,
				currentPage: 0
			};
		}
		case SET_PAGE: {
			const searchedData = state.searchTerm.length > 0 ? doSearch(state.searchTerm, state.searchColumns, state.data) : state.data;
			const filteredData = state.additionalFiltering.length > 0 ? doFiltering(state.additionalFiltering, state.searchColumns, searchedData) : searchedData;
			const { paginatedData, pageCount } = applyPagination(filteredData, { ...state, currentPage: action.page });
			return {
				...state,
				filtered: paginatedData,
				pageCount,
				currentPage: action.page
			};
		}
		case REPLACE_ITEM: {
			const data = doReplaceItem(state.data, action.payload, state.key);
			const searchedData = state.searchTerm.length > 0 ? doSearch(state.searchTerm, state.searchColumns, data) : data;
			const filteredData = state.additionalFiltering.length > 0 ? doFiltering(state.additionalFiltering, state.searchColumns, searchedData) : searchedData;
			const { paginatedData, pageCount } = applyPagination(filteredData, { ...state });
			return {
				...state,
				data: data,
				filtered: paginatedData,
				pageCount
			};
		}
		case SET_LIMIT: {
			const dataToPaginate = state.searchTerm.length > 0 ? state.filtered : state.data;
			const { paginatedData, pageCount } = applyPagination(dataToPaginate, { ...state, currentPage: 0, limit: action.limit });

			return {
				...state,
				filtered: paginatedData,
				pageCount,
				limit: action.limit,
				currentPage: 0
			};
		}
		case ITEM_SELECT:
			return {
				...state,
				selected: selectItem(action.id, state.selected)
			};
		case CLEAR_SELECTED:
			return {
				...state,
				selected: []
			};
		case SELECTED_SET:
			return {
				...state,
				selected: [...action.selected]
			};
		case SORT: {
			const data = applyFilters(
				state.searchTerm.length ? state.searched : state.data,
				{ ...state, sortBy: action.sortBy, sortDirection: action.sortBy.sortDirection }
			);
			const { paginatedData, pageCount } = applyPagination(data, state);

			return {
				...state,
				// data,
				filtered: paginatedData,
				pageCount,
				sortBy: action.sortBy
			};
		}
		default:
			return state;
	}
}

export default function useDataCollection(config) {
	const [state, dispatch] = useReducer(reducer, {
		...initialState,
		...config
	});

	return {
		...state,
		search: (term, additionalFiltering) => {
			dispatch({ type: SEARCH, searchTerm: term, additionalFiltering: additionalFiltering });
		},
		setData: (data, page) => {
			dispatch({ type: DATA_SET, data, page });
		},
		selectItem: id => {
			dispatch({ type: ITEM_SELECT, id });
		},
		clearSelected: () => {
			dispatch({ type: CLEAR_SELECTED });
		},
		setSelected: selected => {
			dispatch({ type: SELECTED_SET, selected });
		},
		setPage: ({ selected }) => {
			dispatch({ type: SET_PAGE, page: selected });
		},
		setLimit: limit => {
			dispatch({ type: SET_LIMIT, limit });
		},
		setSort: sortBy => {
			dispatch({ type: SORT, sortBy });
		},
		replaceItem: (payload) => {
			dispatch({ type: REPLACE_ITEM, payload });
		}
	}
}

/**
 * Apply pagination logic to data set. Only applied if limit property is found in state.
 * @param {array} data
 * @param {object} state
 */
const applyPagination = (data, state) => {
	const { limit, currentPage } = state;
	let paginatedData = [...data];
	let pageCount = 0;

	if (limit) {
		const indexOfLastItem = (currentPage + 1) * limit;
		const indexOfFirstItem = indexOfLastItem - limit;
		paginatedData = paginatedData.slice(indexOfFirstItem, indexOfLastItem);
		pageCount = Math.ceil(data.length / limit);
	}
	return { paginatedData, pageCount };
};

/**
 *
 * @param {*} id
 * @param {*} selected
 */
const applyFilters = (data, state) => {
	let { sortBy, sortDirection, /* filters */ } = state;

	if (sortBy) {
		if (sortBy.type === 'string') {
			data = data.sort((itemA, itemB) => {
				let aValue = itemA[sortBy.column];
				let bValue = itemB[sortBy.column];
				if (!aValue) aValue = '';
				if (!bValue) bValue = '';
				aValue = aValue.toString().toUpperCase();
				bValue = bValue.toString().toUpperCase();

				if (aValue === bValue) {
					return 0;
				} else if (aValue === '') {
					return 1;
				} else if (bValue === '') {
					return -1;
				}

				if (sortDirection === 'desc') {
					return aValue.localeCompare(bValue)
				} else {
					return bValue.localeCompare(aValue)
				}
			});
		} else if (sortBy.type === 'date') {
			data = data.sort((a, b) => {
				const aValue = moment(a[sortBy.column]).unix();
				const bValue = moment(b[sortBy.column]).unix();

				if (sortDirection === 'desc') {
					return bValue - aValue;
				} else {
					return aValue - bValue;
				}
			});
		} else {
			data = data.sort((a, b) => {

				if (a[sortBy.column] === b[sortBy.column]) {
					return 0;
				} else if (a[sortBy.column] === null) {
					return 1;
				} else if (b[sortBy.column] === null) {
					return -1;
				}

				if (sortDirection === 'desc') {
					return b[sortBy.column] - a[sortBy.column];
				} else {
					return a[sortBy.column] - b[sortBy.column];
				}

			});
		}
	}

	return data;
};

/**
 * Toggle selection of item (id).
 *
 * @param {mixed} id
 * @param {array} selected
 */
const selectItem = (id, selected) => {
	if (selected.findIndex(s => s.toString() === id.toString()) > -1) {
		return selected.filter(item => item.toString() !== id.toString());
	} else {
		return [...selected, id];
	}
}

/**
 * Replaces item in state data to payload
 *
 * @param {object} payload
 */
const doReplaceItem = (stateData, payload, property) => {
	const newData = [...stateData];
	const oldIndex = newData.findIndex(d => d[property] === payload[property]);

	//check that payload is found with given property
	if (oldIndex >= 0) {
		newData.splice(oldIndex, 1, payload);
		return newData;
	}
	return stateData;
}

/**
 * Search for items in data array.
 *
 * @param {string} searchTerm
 * @param {array} searchColumns
 * @param {array} data
 */
const doSearch = (searchTerm, searchColumns, data) => {
	if (!Array.isArray(searchColumns)) {
		console.error('searchColumns parameter should be an array');
		return data;
	}

	if (searchTerm && searchTerm.length >= 2) {
		let output = data.filter(item => {
			for (let col of searchColumns) {
				if (item[col] && typeof item[col] === 'string') {
					if (col === 'visitorStatus') {
						// need to check visitor lists visitorStatus separate, because value is without spaces. And there are overlapping values
						// e.g toBeInvited and Invited. For this reason .includes() does not work, because searchTerm "invited" is true in both cases.
						// searchTerm is removed from all spaces and is strictly checked to match column value
						// e.g "To be invited" matches column value "toBeInvited"
						if (item[col].toLowerCase() === searchTerm.replace(/\s/g, '').toLowerCase()) {
							return true;
						}
					} else {
						if (item[col].toLowerCase().includes(searchTerm.toLowerCase())) {
							return true;
						}
					}
				}


			}
			return false;
		});

		return output;
	}
	return data;
};

/**
 * Filter items with id array
 *
 * @param {array} additionalFiltering
 * @param {array} searchColumns
 * @param {array} data
 */
const doFiltering = (additionalFiltering, searchColumns, data) => {
	if (!Array.isArray(searchColumns)) {
		console.error('searchColumns parameter should be an array');
		return data;
	}

	if (additionalFiltering && additionalFiltering.length > 0) {
		let output = data.filter(item => {
			for (let col of searchColumns) {
				if (item[col] && additionalFiltering.includes(item[col])) {
					return true;
				}
			}
			return false;
		});

		return output;
	}
	return data;
};
